import { useState, useCallback, useMemo } from 'react';
import { CallBackProps, STATUS } from 'react-joyride';
import { DASHBOARD_TOUR_STEPS } from './dashboardTourSteps';
import { CMS_TOUR_STORAGE_KEYS } from './constants';

export function useCmsDashboardTour(
  totalProjects: number,
  isFullyLoaded: boolean,
  templatesVisible?: boolean,
  userInfoOnboardingDialogOpen?: boolean,
  suppressed?: boolean
) {
  const [run, setRun] = useState(false);
  const completed =
    typeof window !== 'undefined' &&
    localStorage.getItem(CMS_TOUR_STORAGE_KEYS.DASHBOARD);

  const steps = useMemo(() => {
    if (templatesVisible) return DASHBOARD_TOUR_STEPS;
    return DASHBOARD_TOUR_STEPS.filter(
      (s) => s.target !== '[data-tour="template-stories"]'
    );
  }, [templatesVisible]);

  // Don't start or run the tour while the onboarding questions dialog is open; re-checked on mount and when any input changes
  const [gate, setGate] = useState<{
    isFullyLoaded: boolean;
    completed: string | false | null;
    totalProjects: number;
    userInfoOnboardingDialogOpen?: boolean;
    suppressed?: boolean;
  } | null>(null);
  if (
    gate === null ||
    gate.isFullyLoaded !== isFullyLoaded ||
    gate.completed !== completed ||
    gate.totalProjects !== totalProjects ||
    gate.userInfoOnboardingDialogOpen !== userInfoOnboardingDialogOpen ||
    gate.suppressed !== suppressed
  ) {
    setGate({ isFullyLoaded, completed, totalProjects, userInfoOnboardingDialogOpen, suppressed });
    if (userInfoOnboardingDialogOpen) {
      setRun(false);
    } else if (isFullyLoaded && !completed && !suppressed && totalProjects <= 1) {
      setRun(true);
    }
  }

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      localStorage.setItem(CMS_TOUR_STORAGE_KEYS.DASHBOARD, 'true');
      setRun(false);
    }
  }, []);

  const startTour = useCallback(() => setRun(true), []);

  return { run, steps, handleJoyrideCallback, startTour };
}
